import React, { useEffect, useState } from 'react';
import { getUserById, updateUserById } from '../../../../crud/users_api/users';
import './UserProfile.scss';
import { toast } from 'react-toastify';
import { User } from '../../../Restaurant/Configuracion/types';
import { getPuestoById } from '../../../../crud/management_api/puestos';
import { getLocalByLocalId } from '../../../../crud/management_api/locales';
import { getRoleById } from '../../../../crud/configuration_api/roles';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { Form } from 'react-bootstrap';
import { parseISO, differenceInDays } from 'date-fns';
import { FaEye, FaEyeSlash, FaInfoCircle } from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Button from 'react-bootstrap/Button';
import { FaCheck, FaTimes} from 'react-icons/fa';
import { initUiPreferences, setUiAppZoom, setUiBaseFontSize, uiPrefBounds } from '../../../../utils/uiPreferences';
import { isCacheEnabled, setCacheEnabled } from '../../../../crud/cache/settings';

const UserProfile: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState<boolean>(true);
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [puesto, setPuesto] = useState<any | null>(null);
  const [local, setLocal] = useState<any | null>(null);
  const [rol, setRol] = useState<any | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editableValue, setEditableValue] = useState<string | null>(null);
  const [editableDate, setEditableDate] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [originalValue, setOriginalValue] = useState<string | null>(null);
  // UI Prefs state
  const [baseFontPx, setBaseFontPx] = useState<number>(uiPrefBounds.font.def);
  const [appZoom, setAppZoom] = useState<number>(uiPrefBounds.zoom.def);
  const [cacheEnabled, setCacheEnabledState] = useState<boolean>(false);

  useEffect(() => {
    // Load UI prefs early for local state
    (async () => {
      await initUiPreferences();
      // Load cache setting (defaults to true if unset)
      try {
        const ce = await isCacheEnabled();
        setCacheEnabledState(!!ce);
      } catch {
        // If reading persisted setting fails, default to disabled
        setCacheEnabledState(false);
      }
      // Read back applied values from document to reflect actual state
      const fs = parseFloat(getComputedStyle(document.documentElement).fontSize || '13.5');
      const az = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--app-zoom') || '1');
      setBaseFontPx(Number.isFinite(fs) ? fs : uiPrefBounds.font.def);
      setAppZoom(Number.isFinite(az) ? az : uiPrefBounds.zoom.def);
    })();

    const userContext = localStorage.getItem('user');
    const userId = userContext ? JSON.parse(userContext).user_id : 0;
    const fetchUser = async () => {
      try {
        const response = await getUserById(userId);
        setUser(response.data);
      } catch (err) {
        setError('Error fetching user data');
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      const fetchPuesto = async () => {
        try {
          const response = await getPuestoById(Number(user.puesto_id));
          setPuesto(response.data);
        } catch (err) {
          toast.error('Error fetching puesto data');
        }
      }
      const fetchLocal = async () => {
        try {
          const response = await getLocalByLocalId(user.local_id);
          setLocal(response.data);
        } catch (err) {
          toast.error('Error fetching local data');
        }
      }
      const fetchRol = async () => {
        try {
          const response = await getRoleById(Number(user.rol_id));
          setRol(response.data);
        } catch (err) {
          toast.error('Error fetching rol data');
        }
      }
      fetchRol();
      fetchLocal();
      fetchPuesto();
      setLoadingData(false);
    }
  }, [user]);

  const handleEditField = (field: string, value: any) => {
    setEditingField(field);
    setEditableValue(value);
    setOriginalValue(value);
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableValue(e.target.value);
  };

  const handleFieldBlur = (field: string) => {
    if (editableValue !== originalValue) {
      const userId = user ? Number(user.user_id) : 0;
      updateUserById(userId, { [field]: editableValue }).then(response => {
        if (response.status === 200) {
          toast.success('Usuario actualizado con éxito');
          // Actualizar el estado del usuario con los nuevos datos
          setUser(prevUser => prevUser ? { ...prevUser, [field]: editableValue } : null);
          setEditingField(null);
        }
      }).catch(err => {
        toast.error('Error actualizando al usuario');
      });
    } else {
      setEditingField(null);
    }
  };

  const handleDateChange = (date: string) => {
    setEditableDate(date);
  };

  const original_date = (user?.created_at && user) ? parseISO(user?.created_at) : new Date();
  // const formattedDate = (user?.created_at && user) ? format(parseISO(user.created_at), 'yyyy-MM-dd') : '';

  if (loadingUser || loadingData) return <LoadingSpinner text='Cargando datos de Usuario'/>;
  else if (error) return <p>{error}</p>;
  else {
    return (
      <div className="user-profile">
        <h2><span className="highlighted">{differenceInDays(new Date(), original_date)}</span> días juntos!</h2>
        {user && (
          <div className="cards-container">
            <div className="card info-card editable">
              <h3 className="section-title">Información</h3>
              {editingField === 'username' ? (
                <div className="editable-field">
                  <input
                    type="text"
                    value={editableValue || ''}
                    onChange={handleFieldChange}
                    autoFocus
                    className='input-user'
                  />
                  <Button variant='none' className='form-button-create' onClick={() => handleFieldBlur('username')}>
                    <FaCheck/>
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes/>
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('username', user.username)}><strong> Nombre : </strong> {user.username}</p>
              )}
              {editingField === 'middle_name' ? (
                <div className="editable-field">
                  <input
                    type="text"
                    value={editableValue || ''}
                    onChange={handleFieldChange}
                    autoFocus
                    className='input-user'
                  />
                  <Button variant='none' className='form-button-create'>
                    <FaCheck onClick={() => handleFieldBlur('middle_name')} />
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes/>
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('middle_name', user.middle_name)}><strong>Segundo nombre :</strong> {user.middle_name}</p>
              )}
              {editingField === 'last_name' ? (
                <div className="editable-field">
                  <input
                    type="text"
                    value={editableValue || ''}
                    onChange={handleFieldChange}
                    autoFocus
                    className='input-user'
                  />
                  <Button variant='none' className='form-button-create'>
                    <FaCheck onClick={() => handleFieldBlur('last_name')} />
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes />
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('last_name', user.last_name)}><strong>Apellido:</strong> {user.last_name}</p>
              )}
              {editingField === 'dob' ? (
                <div className="editable-field">
                  <Form.Control
                    type="date"
                    value={editableDate || user.dob}
                    onChange={(e) => handleDateChange(e.target.value)}
                    autoFocus
                  />
                  <Button variant='none' className='form-button-create'>
                    <FaCheck onClick={() => handleFieldBlur('dob')} />
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes />
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('dob', user.dob)}><strong>Fecha de nacimiento : </strong> {user.dob}</p>
              )}
              {editingField === 'address' ? (
                <div className="editable-field">
                  <input
                    type="text"
                    value={editableValue || ''}
                    onChange={handleFieldChange}
                    autoFocus
                    className='input-user'
                  />
                  <Button variant='none' className='form-button-create'>
                    <FaCheck onClick={() => handleFieldBlur('address')} />
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes/>
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('address', user.address)}><strong>Dirección : </strong> {user.address}</p>
              )}
              {editingField === 'email' ? (
                <div className="editable-field">
                  <input
                    type="text"
                    value={editableValue || ''}
                    onChange={handleFieldChange}
                    autoFocus
                    className='input-user'
                  />
                  <Button variant='none' className='form-button-create'>
                    <FaCheck onClick={() => handleFieldBlur('email')} />
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes />
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('email', user.email)}><strong>Email:</strong> {user.email}</p>
              )}
              {editingField === 'password' ? (
                <div className="editable-field password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={editableValue || ''}
                    onChange={handleFieldChange}
                    autoFocus
                    className='input-user'
                  />
                  <span onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                  <Button variant='none' className='form-button-create'>
                    <FaCheck onClick={() => handleFieldBlur('password')} />
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes />
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('password', user.password)}>
                  <strong>Clave:</strong> 
                  <span className="password-dots">  ••••••••</span> 
                  <FaEye onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer', marginLeft: '10px' }} />
                </p>
              )}
              {editingField === 'phone_number' ? (
                <div className="editable-field">
                  <input
                    type="text"
                    value={editableValue || ''}
                    onChange={handleFieldChange}
                    autoFocus
                    className='input-user'
                  />
                  <Button variant='none' className='form-button-create'>
                    <FaCheck onClick={() => handleFieldBlur('phone_number')} />
                  </Button>
                  <Button variant='none' className='form-button-cancel' onClick={() => setEditingField(null)}>
                    <FaTimes />
                  </Button>
                </div>
              ) : (
                <p onClick={() => handleEditField('phone_number', user.phone_number)}><strong>Teléfono:</strong> {user.phone_number}</p>
              )}

              <hr className="section-divider" />

              <div className="info-list">
                <p className="info-icon">
                  <strong>Puesto:</strong> {puesto?.puesto_name}
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip id="tooltip-puesto">{puesto?.puesto_description}</Tooltip>}
                  >
                    <span style={{ cursor: 'pointer', marginLeft: 6 }}><FaInfoCircle size={14}/></span>
                  </OverlayTrigger>
                </p>
              </div>
              
              <div className="info-list">
                <p className="info-icon">
                  <strong>Local:</strong> {local?.local_name}
                </p>
                <p><strong>Estado:</strong> {user?.state ? 'Activo' : 'Inactivo'}</p>
                <p className="info-icon">
                  <strong>Rol:</strong> {rol?.rol_name}
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip id="tooltip-rol">{rol?.rol_description}</Tooltip>}
                  >
                    <span style={{ cursor: 'pointer', marginLeft: 6 }}><FaInfoCircle size={14}/></span>
                  </OverlayTrigger>
                </p>
              </div>
            </div>
            <div className="card prefs-card non-editable">
              <h3 className="section-title">Preferencias personales</h3>
              <div className="pref-row">
                <label className="pref-label">Cache de API</label>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={cacheEnabled}
                    onChange={async (e) => {
                      const enabled = e.target.checked;
                      setCacheEnabledState(enabled);
                      await setCacheEnabled(enabled);
                    }}
                  />
                  <span className="slider round"></span>
                </label>
                <div></div>
              </div>
              <div className="pref-row">
                <label className="pref-label">Tamaño de fuente base</label>
                <input
                  className="pref-range"
                  type="range"
                  min={uiPrefBounds.font.min}
                  max={uiPrefBounds.font.max}
                  step={0.5}
                  value={baseFontPx}
                  onChange={async (e) => {
                    const v = parseFloat(e.target.value);
                    setBaseFontPx(v);
                    await setUiBaseFontSize(v);
                  }}
                />
                <div className="pref-input">
                  <input
                    type="number"
                    value={Number(baseFontPx.toFixed(1))}
                    min={uiPrefBounds.font.min}
                    max={uiPrefBounds.font.max}
                    step={0.5}
                    onChange={async (e) => {
                      const v = parseFloat(e.target.value);
                      setBaseFontPx(v);
                      await setUiBaseFontSize(v);
                    }}
                  />
                  <span className="unit">px</span>
                </div>
              </div>

              <div className="pref-row">
                <label className="pref-label">Zoom global</label>
                <input
                  className="pref-range"
                  type="range"
                  min={uiPrefBounds.zoom.min}
                  max={uiPrefBounds.zoom.max}
                  step={0.05}
                  value={appZoom}
                  onChange={async (e) => {
                    const v = parseFloat(e.target.value);
                    setAppZoom(v);
                    await setUiAppZoom(v);
                  }}
                />
                <div className="pref-input">
                  <input
                    type="number"
                    value={Number(appZoom.toFixed(2))}
                    min={uiPrefBounds.zoom.min}
                    max={uiPrefBounds.zoom.max}
                    step={0.05}
                    onChange={async (e) => {
                      const v = parseFloat(e.target.value);
                      setAppZoom(v);
                      await setUiAppZoom(v);
                    }}
                  />
                  <span className="unit">x</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default UserProfile;
